import jsPDF from "jspdf";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Table
} from "reactstrap";
import Swal from "sweetalert2";
import ButtonBack from "../../Components/ButtonBack";
import Loading from "../../Components/Loading";
import { PrimaryLogo } from "../../Components/Logo/PrimaryLogo";
import CommissionDetailTable from "../../Components/Tables/CommissionDetailTable/CommissionDetailTable";
import { DASHBOARD_USER_ROLES } from "../../constants";
import { useCurrentUser } from "../../slices/auth/hooks";
import {
  getCommissionById,
  getCommissionByIdAdmin,
  payCommissionAdmin,
} from "../../slices/general/thunk";
import {
  capitalizeFirstLetter,
  formatDateForInvoice,
  formatTimeForInvoice,
  parsedInvoiceAmount
} from "../../utils/utils";

const CommissionDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const locations = useLocation();
  const currentUser = useCurrentUser();
  const globalTheme = useSelector(state => state.Theme);
  const isWhitelabel = globalTheme?.whitelabelActive;

  const isAdmin = currentUser.role === DASHBOARD_USER_ROLES.ADMIN;

  // Dynamic contact info for whitelabel
  const displayAddress = isWhitelabel
    ? (globalTheme?.contactInfo?.companyAddress || globalTheme?.contactInfo?.companyName || "")
    : "4625 Lange Avenue NE, United States";
  const displayAddressLine2 = isWhitelabel ? "" : "St. Michael, MN 55376";
  const displayEmail = isWhitelabel
    ? (globalTheme?.emailConfig?.supportEmail || "support@baxrx.com")
    : "support@baxrx.com";
  const displayWebsite = isWhitelabel && globalTheme?.customDomains?.[0]
    ? `https://${globalTheme.customDomains[0]}`
    : "https://baxrx.com";

  const [loading, setLoading] = useState(false);

  const [loadingPay, setLoadingPay] = useState(false);

  const [commission, setCommission] = useState([]);

  const downloadCommissionPDF = (commission) => {
    if (!commission || !commission.items) {
      console.error("Commission data is incomplete or not loaded yet.");
      return;
    }

    const doc = new jsPDF();
    const date = commission.date;
    const commissionMonthLabel = moment(date).format("MMMM");
    const commissionYear = moment(date).format("YYYY");
    const totalAmountFormatted = (commission.totalAmount / 100).toFixed(2);

    doc.setFontSize(20);
    doc.text(
      `Commission - ${commissionMonthLabel} - ${commissionYear}`,
      20,
      20
    );

    doc.setFontSize(16);
    doc.text("BaxRx Commissions", 20, 30);

    doc.setFontSize(14);
    doc.setTextColor(56, 56, 56);
    doc.text(`Commission ID: ${commission.id}`, 20, 50);
    doc.text(`Total Amount: $${totalAmountFormatted}`, 160, 50);

    doc.setFontSize(18);
    doc.setTextColor(33, 33, 33);
    doc.text(`${commission.paid ? "Paid" : "Pending"}`, 160, 60);

    if (commission.paid) {
      doc.setFillColor(204, 255, 204); // Green for paid
      doc.rect(20, 70, 170, 10, "F");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Payment Received on ${moment(date).format("MM/DD/YYYY")}`,
        25,
        77
      );
    } else {
      doc.setFillColor(255, 204, 204); // Red for pending
      doc.rect(20, 70, 170, 10, "F");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Due on ${moment(date).format("MM/DD/YYYY")}`, 25, 77);
    }

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text("COMMISSION DETAILS", 20, 95);

    let currentY = 105;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("DETAILS", 20, currentY);
    doc.text("AMOUNT", 180, currentY, null, null, "right");

    currentY += 10;
    doc.setDrawColor(0);
    doc.line(20, currentY, 190, currentY);
    currentY += 10;

    commission.items.forEach((item, index) => {
      doc.setFontSize(12);
      doc.text(`${index + 1}. ${item.description}`, 20, currentY);
      doc.text(
        `$${(item.amount / 100).toFixed(2)}`,
        180,
        currentY,
        null,
        null,
        "right"
      );
      currentY += 10;
    });

    const fileName = `BaxRx-Commission-${commissionMonthLabel}-${commissionYear}.pdf`;
    doc.save(fileName);
  };

  const fecthCommissionById = async () => {
    try {
      setLoading(true);
      const res = await dispatch(
        getCommissionById({
          userId: currentUser.id,
          commissionId: id,
        })
      );
      if (!res.payload || res.payload.error) {
        Swal.fire({
          title: "Error",
          text: "Commission not found",
          icon: "error",
        });
        navigate("/commissions");
      }
      setCommission(res.payload);
      setLoading(false);
    } catch (error) {
      setLoading(true);
      console.log(error);
      setLoading(false);
    }
  };

  const fecthCommissionByIdAdmin = async () => {
    try {
      setLoading(true);
      const res = await dispatch(
        getCommissionByIdAdmin({
          commissionId: id,
        })
      );
      console.log(res);
      if (!res.payload || res.payload.error) {
        Swal.fire({
          title: "Error",
          text: "Commission not found",
          icon: "error",
        });
        navigate("/commissions");
      }
      setCommission(res.payload);
      setLoading(false);
    } catch (error) {
      setLoading(true);
      console.log(error);
      setLoading(false);
    }
  };

  const payCommissionByIdAdmin = async () => {
    try {
      setLoadingPay(true);
      const res = await dispatch(
        payCommissionAdmin({
          commissionId: id,
        })
      );
      console.log(res);

      if (!res.payload || res.payload.error) {
        Swal.fire({
          title: "Error",
          text: `${res.payload.error || "Error paying commission"}`,
          icon: "error",
        });
      } else {
        Swal.fire({
          title: "Success",
          text: "Commission paid successfully",
          icon: "success",
        });
        fecthCommissionByIdAdmin();
      }
      setLoadingPay(false);
    } catch (error) {
      setLoadingPay(true);
      console.log(error);
      setLoadingPay(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fecthCommissionByIdAdmin();
    } else {
      fecthCommissionById();
    }
  }, []);

  const downloadInvoice = () => {
    const doc = new jsPDF();

    const pageHeight = doc.internal.pageSize.height - 20; // Adjust for margins
    const lineHeight = 10; // Default line height for level 0
    const subLineHeight = 8; // Line height for sublevels
    let currentY = 20; // Starting Y position for each page

    const date = commission.effectiveForDate;
    const transactionDate = moment.unix(commission.paymentDate || commission.createdAtUnix).utc().format("MM/DD/YYYY");
    const invoiceMonthLabel = moment.utc(date).format("MMMM");
    const invoiceYear = moment.utc(date).format("YYYY");
    const totalAmountFormatted = (commission.totalAmount / 100).toFixed(2);
    const resolveToName = // If it has no firstname and last name, use company name
      commission.dashboardUserInfo?.firstName && commission.dashboardUserInfo?.lastName
        ? `${commission.dashboardUserInfo?.firstName} ${commission.dashboardUserInfo?.lastName}`
        : commission.dashboardUserInfo?.companyName;

    const addPageIfNeeded = (height) => {
      if (currentY + height > pageHeight) {
        doc.addPage();
        currentY = 20; // Reset Y position for the new page
      }
    };

    const renderHeader = () => {
      doc.setFontSize(20);
      doc.text(`Commission Statement - ${invoiceMonthLabel} - ${invoiceYear}`, 20, currentY);
      currentY += 10;

      doc.setFontSize(16);
      doc.text("BaxRx", 20, currentY);
      currentY += 10;

      doc.setFontSize(12);

      doc.text("St. Michael, MN", 20, currentY);

      doc.setFontSize(10);
      doc.text(
        `To: ${resolveToName}`
        , 130, currentY);
      currentY += 5;
      // Put email below
      doc.text(
        `${commission.dashboardUserInfo?.email}`
        , 130, currentY);
      // Phone number
      currentY += 5;
      doc.text(
        `${commission.dashboardUserInfo?.phoneNumber || ''}`
        , 130, currentY);
      // Full address
      currentY += 5;
      doc.text(
        `${commission.dashboardUserInfo?.fullAddress || ''}`
        , 130, currentY);

      // Go back to original Y position
      currentY -= 15;

      currentY += 5;

      doc.setFontSize(12);
      doc.text("612-964-1898", 20, currentY);
      currentY += 5;

      doc.setFontSize(12);
      doc.text("Merchant Org ID: 234927", 20, currentY);
      currentY += 5;

      doc.setFontSize(12);
      doc.text("Merchant ID: 346601", 20, currentY);
      currentY += 10;

      doc.setFontSize(10);
      doc.text(
        `Date of Transaction: ${transactionDate}`
        , 20, currentY);
      currentY += 5;

      if (commission?.dexTransactionInfo?.transaction_id) {
        doc.text(
          `Transaction ID: ${commission?.dexTransactionInfo?.transaction_id}`
          , 20, currentY);
        currentY += 10;
      }



      doc.setFontSize(14);
      doc.setTextColor(56, 56, 56);
      doc.text("Invoice Balance", 20, currentY);
      doc.text("Payment Status", 160, currentY);
      currentY += 10;

      doc.setFontSize(18);
      doc.setTextColor(33, 33, 33);
      doc.text(`$${totalAmountFormatted}`, 20, currentY);
      doc.text(`${commission.paid ? "Paid" : "Due"}`, 160, currentY);
      currentY += 15; // Add space after header
    };

    const renderInvoiceItems = (items, parentIndex = [], level = 0) => {
      const marginPerLevel = 10;

      items.forEach((item, index) => {
        const currentIndex = [...parentIndex, index + 1];
        const itemIndex = currentIndex.join(".");
        const itemDescription = item.description;

        doc.setFontSize(level === 0 ? 12 : 10); // Adjust font size for sublevels
        const textX = 30 + level * marginPerLevel;
        const textWidth = 140 - level * marginPerLevel;

        const lines = doc.splitTextToSize(itemDescription, textWidth);

        // Add a page if the content exceeds the current page
        addPageIfNeeded(lines.length * (level === 0 ? lineHeight : subLineHeight));

        doc.text(`${itemIndex}`, 20, currentY);
        doc.text(lines, textX, currentY);
        doc.text(
          `$${(item.amount / 100).toFixed(2)}`,
          180,
          currentY,
          null,
          null,
          "right"
        );

        currentY += lines.length * (level === 0 ? lineHeight : subLineHeight);

        if (item.items && item.items.length > 0) {
          renderInvoiceItems(item.items, currentIndex, level + 1);
        }
      });
    };

    const renderFooter = () => {
      addPageIfNeeded(20); // Ensure space for footer

      doc.setFontSize(14);
      doc.text("Total Amount", 20, currentY + 10);
      doc.text(
        `$${totalAmountFormatted}`,
        180,
        currentY + 10,
        null,
        null,
        "right"
      );
    };

    // Render the document
    renderHeader();
    renderInvoiceItems(commission.items);
    renderFooter();

    const fileName = `BaxRx - Commission Statement - ${invoiceMonthLabel} ${invoiceYear}.pdf`;
    doc.save(fileName);
  };
  const renderBillignAddress = (commission) => {
    const dashboardUserInfo = commission.dashboardUserInfo;
    const name = commission.dashboardUserInfo?.companyName
      ? `${dashboardUserInfo?.companyName} `
      : `${dashboardUserInfo?.firstName} ${dashboardUserInfo?.lastName}`;
    return (
      <Col lg={12}>
        <CardBody className="p-4 border-top border-top-dashed">
          <Row className="g-3">
            <Col sm={3} className="border rounded-4 p-3">
              <p className="text-dark mb-2 text-uppercase fw-normal">
                Information
              </p>
              <p className="fw-medium mb-2" id="billing-name">
                {name}
              </p>
              <p className="text-muted mb-1" id="billing-address-line-1">
                {dashboardUserInfo?.companyAddress}
              </p>
              {dashboardUserInfo?.email && (
                <p className="text-muted mb-1">
                  <span>Email: </span>
                  <span id="billing-phone-no">{dashboardUserInfo?.email}</span>
                </p>
              )}
              {dashboardUserInfo?.role && (
                <p className="text-muted mb-0">
                  <span>Type: </span>
                  <span id="billing-tax-no">
                    {capitalizeFirstLetter(dashboardUserInfo.role)}
                  </span>
                </p>
              )}
            </Col>
            {/* <Col sm={6}>
              <h6 className="text-muted text-uppercase fw-semibold mb-3">
                Shipping Address
              </h6>
              <p className="fw-medium mb-2" id="shipping-name">
                David Nichols
              </p>
              <p className="text-muted mb-1" id="shipping-address-line-1">
                305 S San Gabriel Blvd
              </p>
              <p className="text-muted mb-1">
                <span>Phone: +</span>
                <span id="shipping-phone-no">(123) 456-7890</span>
              </p>
            </Col> */}
          </Row>
        </CardBody>
      </Col>
    );
  };

  const renderButtonPayNow = (commission) => {
    if (loading) {
      return null;
    }

    return (
      <>
        {!commission.paid && isAdmin ? (
          <Button
            onClick={() => payCommissionByIdAdmin()}
            color="primary"
            className="btn btn-primary"
            disabled={loadingPay}
          >
            <i className="ri-add-line align-bottom me-1"></i> Pay
          </Button>
        ) : null}{" "}
      </>
    );
  };

  // const renderCollapse = (items, parentIndex = []) => {
  //   const marginPerLevel = 15;
  //   return items?.map((item, index) => {
  //     const currentIndex = [...parentIndex, index];
  //     const isOpen = currentIndex.every(
  //       (val, idx) => isCollapseOpen[idx] === val
  //     );
  //     const hasInnerItems = item.items && item.items.length > 0;

  //     const itemIndex =
  //       parentIndex.length > 0
  //         ? `${parentIndex.join(".")}.${index + 1}`
  //         : index + 1;

  //     return (
  //       <>
  //         <tr
  //           className={`${
  //             hasInnerItems ? "hover-table-effect" : "no-hover-effect"
  //           } ${isOpen ? "bg-light" : ""}`}
  //           onClick={(e) => {
  //             if (hasInnerItems)
  //               toggleCollapse(index, hasInnerItems, parentIndex);
  //           }}
  //           style={{ cursor: hasInnerItems ? "pointer" : "default" }}
  //         >
  //           <th
  //             scope="row"
  //             className="text-start "
  //             style={{
  //               paddingLeft: `${parentIndex.length * marginPerLevel}px`,
  //             }}
  //           >
  //             {formatIndexLabelCollapse(itemIndex)}
  //           </th>
  //           <td className="text-start">
  //             <span className="fw-medium ">{item.description}</span>
  //             {item.type && (
  //               <p className="text-muted mb-0">
  //                 {commissionDetailsType[item.type]}
  //               </p>
  //             )}
  //           </td>
  //           <td className="text-end">
  //             ${parsedInvoiceAmount(item.total || item.amount)}
  //           </td>
  //           {hasInnerItems ? (
  //             <td className="text-end" style={{ width: "20px" }}>
  //               <i
  //                 className={`fw-semibold fs-5  ri-${
  //                   isOpen ? "close-line" : "add-line"
  //                 } icon-toggle`}
  //                 style={{
  //                   transform: isOpen ? "rotate(91deg)" : "rotate(0deg)",
  //                 }}
  //               ></i>
  //             </td>
  //           ) : (
  //             <td className="text-end" style={{ width: "20px" }}></td>
  //           )}
  //         </tr>
  //         {hasInnerItems && isOpen && renderCollapse(item.items, currentIndex)}
  //       </>
  //     );
  //   });
  // };

  // const renderCommissionDetailsTable = (commission) => {
  //   return commission.items?.map((item, index) => {
  //     const hasInnerItems = item.items && item.items.length > 0;
  //     const isOpen = isCollapseOpen.includes(index);

  //     return (
  //       <div className="table-responsive">
  //         <Table
  //           className={`table-borderless text-center  table-nowrap align-middle mb-0 ${
  //             hasInnerItems ? "" : ""
  //           }`}
  //         >
  //           <thead>
  //             <tr className="bg bg-light">
  //               <th
  //                 className="text-start"
  //                 scope="col"
  //                 style={{ width: "100px" }}
  //               >
  //                 #
  //               </th>
  //               <th className="text-start" scope="col">
  //                 Commissions Details
  //               </th>
  //               <th scope="col" className="text-end">
  //                 Amount
  //               </th>
  //               <th scope="col" className="text-end"></th>
  //             </tr>
  //           </thead>

  //           <tbody
  //             className={hasInnerItems ? "cursor-pointer" : "cursor-default"}
  //             id="products-list"
  //           >
  //             <tr
  //               className={`${
  //                 hasInnerItems ? "hover-table-effect" : "no-hover-effect"
  //               } ${isOpen ? "bg-light" : ""}`}
  //               style={{ cursor: hasInnerItems ? "pointer" : "default" }}
  //               onClick={
  //                 hasInnerItems ? () => toggleCollapse(index) : undefined
  //               }
  //             >
  //               <th scope="row" className="text-start">
  //                 {index + 1}
  //               </th>
  //               <td className="text-start">
  //                 <span className="fw-medium text-start">
  //                   {item.description}
  //                 </span>
  //                 {item.type && (
  //                   <p className="text-muted mb-0">
  //                     {commissionDetailsType[item.type]}
  //                   </p>
  //                 )}
  //               </td>
  //               <td className="text-end">
  //                 ${parsedInvoiceAmount(item.total || item.amount)}
  //               </td>
  //               {hasInnerItems && (
  //                 <td className="text-end" style={{ width: "20px" }}>
  //                   <i
  //                     className={`fw-semibold fs-5 ri-${
  //                       isOpen ? "close-line" : "add-line"
  //                     } icon-toggle`}
  //                     style={{
  //                       transform: isOpen ? "rotate(91deg)" : "rotate(0deg)",
  //                     }}
  //                   ></i>
  //                 </td>
  //               )}
  //             </tr>
  //             {hasInnerItems && isOpen && renderCollapse(item.items, [index])}
  //           </tbody>
  //         </Table>
  //       </div>
  //     );
  //   });
  // };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between">
          <div>
            <ButtonBack />
          </div>
          {/* <div className="ms-auto">{renderButtonPayNow(commission)}</div> */}
        </div>

        <Row className="justify-content-center mt-3 position-relative">
          {(loadingPay || loading) && <Loading />}
          <Col xxl={12}>
            <Card id="demo" className="border">
              <Row>
                <Col lg={12}>
                  <CardHeader className="border-bottom-dashed p-4">
                    <div className="d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-center">

                        <PrimaryLogo />

                        <div className="hstack gap-2 justify-content-end d-print-none mt-4">
                          <div
                            onClick={downloadInvoice}
                            className="btn btn-primary"
                          >
                            <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                            Download
                          </div>

                          {renderButtonPayNow(commission)}
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-5">
                        <div
                          style={{ background: "var(--thead-bg)" }}
                          className="rounded-4 p-3 col-3">
                          <h6 className="text-dark text-uppercase fw-semibold">
                            Address
                          </h6>
                          <p className="text-muted mb-1" id="address-details">
                            {displayAddress}
                          </p>
                          {displayAddressLine2 && (
                            <p className="text-muted mb-0" id="zip-code">
                              <span>{displayAddressLine2}</span>
                            </p>
                          )}
                        </div>
                        <div className="text-end bg-light text-primary rounded-4 p-3">
                          <h6 className="mb-2 d-flex align-items-center">
                            <i className="ri-mail-line me-2 fs-4 text-primary"></i>
                            <span
                              id="email"
                              className="text-primary fw-semibold fs-6"
                            >
                              {displayEmail}
                            </span>
                          </h6>
                          <h6 className="mb-0 d-flex align-items-center">
                            <i className="ri-global-line me-2 fs-4 text-primary"></i>
                            <Link
                              to={displayWebsite}
                              target="_blank"
                              className="link-primary text-primary fw-semibold fs-6"
                              id="website"
                            >
                              {displayWebsite}
                            </Link>
                          </h6>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Col>
                <Col lg={12}>
                  <CardBody className="p-4">
                    <Row className="g-3">
                      {commission.id && (
                        <Col
                          md={3}
                          sm={6}
                          xs={12}
                          className="border rounded-4 p-3 mx-2"
                        >
                          <p className="mb-2 text-uppercase fw-normal">
                            Commission No
                          </p>
                          <h5 className="fs-14 mb-0 text-muted">
                            <span id="commission-no">{commission.id}</span>
                          </h5>
                        </Col>
                      )}
                      <Col
                        md={3}
                        sm={6}
                        xs={12}
                        className="border rounded-4 p-3 mx-2"
                      >
                        <p className="text-dark mb-2 text-uppercase fw-normal">
                          Date
                        </p>
                        <h5 className="fs-14 mb-0 fw-normal">
                          <span id="commission-date">
                            {formatDateForInvoice(commission.date)}
                          </span>{" "}
                          <small className="text-muted" id="commission-time">
                            {formatTimeForInvoice(commission.date)}
                          </small>
                        </h5>
                      </Col>
                      <Col
                        md={3}
                        sm={6}
                        xs={12}
                        className="border rounded-4 p-3 mx-2"
                      >
                        <p className="text-dark mb-2 text-uppercase fw-normal">
                          Payment Status
                        </p>
                        <span
                          className={`badge fs-11 badge-soft-${commission?.paid === true
                            ? "success"
                            : commission?.paymentProcessing === true
                              ? "warning"
                              : "warning"
                            }`}
                        >
                          {commission?.paid === true
                            ? "Paid"
                            : commission?.paymentProcessing === true
                              ? "Processing"
                              : "Pending"}
                        </span>
                      </Col>
                      <Col
                        md={3}
                        sm={6}
                        xs={12}
                        className="border rounded-4 p-3 mx-2"
                      >
                        <p className="text-dark mb-2 text-uppercase fw-normal">
                          Total Amount
                        </p>
                        <h5 className="fs-14 mb-0">
                          $
                          <span id="total-amount">
                            {parsedInvoiceAmount(commission.totalAmount)}
                          </span>
                        </h5>
                      </Col>
                    </Row>
                  </CardBody>
                </Col>
                {renderBillignAddress(commission)}
                <Col lg={12}>
                  <CardBody className="p-4">
                    {/* {renderCommissionDetailsTable(commission)} */}
                    <CommissionDetailTable commission={commission} />

                    <div className="border-top border-top-dashed mt-2">
                      <Table
                        className="table table-border table-nowrap align-middle mb-0 ms-auto"
                        style={{ width: "250px" }}
                      >
                        <tbody>
                          <tr className="border-top border-top-dashed fs-15">
                            <th scope="row">Total Amount</th>
                            <th className="text-end">
                              ${parsedInvoiceAmount(commission.totalAmount)}
                            </th>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CommissionDetails;
